//  transfers.js
//  Inter-account transfer detection.
//
//  A transfer is money moving between two of the user's own accounts — e.g.
//  paying a credit card from checking, or moving savings into checking. It
//  shows up as TWO transactions: an outflow (expense) in the source account
//  and an inflow (income or repayment) of equal magnitude in the destination
//  account, within a few days of each other.
//
//  Left untagged these distort the budget: a card payment counts as an
//  "expense" in checking on top of the individual purchases already recorded
//  on the card, and a checking->savings move inflates both income and expense
//  for the month. detectTransfers() returns the Set of transaction ids that
//  belong to a detected transfer pair so callers can exclude them from
//  income/expense totals and the forecast. It is non-destructive — nothing is
//  written to storage; the classification is recomputed from the live data.
//
//  detectTransferPairs() exposes the same matching with its structure intact —
//  which account paid which, how much, and how many days the two legs were
//  apart. That structure is what lets us project a transfer that hasn't
//  happened yet (see card-payments.js): the id Set alone says "these two rows
//  cancel out" and nothing about the route that produced them.

const DAY_MS = 24 * 60 * 60 * 1000;

// 'YYYY-MM-DD' must be read as LOCAL midnight. Parsing it bare puts it at UTC
// midnight, which is the previous day in negative-UTC zones — harmless while
// this module only compared dates to each other (the shift cancels), but the
// pair dates are now consumed directly by the projection.
function parseTime(value) {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`).getTime();
  }
  return new Date(value).getTime();
}

/**
 * Full matching result, structure retained.
 *
 * @param {Array} transactions  app transactions (need `accountId`, `type`, `amount`, `date`, `id`)
 * @param {Object} [opts]
 * @param {number} [opts.dayWindow=4]        max days between the two legs
 * @param {number} [opts.amountTolerance=0.005]  max absolute difference in amount
 * @returns {{
 *   ids: Set<string>,
 *   pairs: Array<{
 *     outflowId: string, inflowId: string,
 *     fromAccountId: string, toAccountId: string,
 *     amount: number, outflowDate: Date, inflowDate: Date, lagDays: number,
 *     outflow: object, inflow: object,
 *   }>,
 *   legs: {outflows: Array, inflows: Array},
 * }}
 */
export function detectTransferPairs(transactions, opts = {}) {
  const dayWindow = opts.dayWindow ?? 4;
  const tolerance = opts.amountTolerance ?? 0.005;
  const ids = new Set();
  const pairs = [];
  const expenses = [];
  const incomes = [];
  if (!Array.isArray(transactions)) return { ids, pairs, legs: { outflows: expenses, inflows: incomes } };

  // Cross-account matching needs an accountId on both legs. Manual/CSV rows
  // without one can't be reliably paired, so we leave them alone.
  for (const t of transactions) {
    if (!t || !t.accountId || t.id == null) continue;
    const amount = Math.abs(Number(t.amount) || 0);
    if (amount === 0) continue;
    const time = parseTime(t.date);
    if (Number.isNaN(time)) continue;
    const rec = { id: t.id, accountId: t.accountId, amount, time, tx: t };
    // A refund is deliberately not a leg. Money the merchant handed back has
    // no counterpart in another of the user's accounts, so offering it as an
    // inflow only invites a false pair with some unrelated outflow of the
    // same size — which would then cancel real spending out of the budget.
    if (t.type === 'expense') expenses.push(rec);
    else if (t.type === 'income' || t.type === 'repayment') incomes.push(rec);
  }

  // Greedy nearest-date pairing: for each outflow, take the closest unused
  // inflow from a DIFFERENT account whose amount matches within tolerance.
  expenses.sort((a, b) => a.time - b.time);
  const usedIncome = new Set();

  for (const exp of expenses) {
    let best = null;
    let bestGap = Infinity;
    for (const inc of incomes) {
      if (usedIncome.has(inc.id)) continue;
      if (inc.accountId === exp.accountId) continue;
      if (Math.abs(inc.amount - exp.amount) > tolerance) continue;
      const gap = Math.abs(inc.time - exp.time);
      if (gap > dayWindow * DAY_MS) continue;
      if (gap < bestGap) {
        best = inc;
        bestGap = gap;
      }
    }
    if (best) {
      ids.add(exp.id);
      ids.add(best.id);
      usedIncome.add(best.id);
      pairs.push({
        outflowId: exp.id,
        inflowId: best.id,
        fromAccountId: exp.accountId,
        toAccountId: best.accountId,
        amount: exp.amount,
        outflowDate: new Date(exp.time),
        inflowDate: new Date(best.time),
        // Signed: positive when the inflow posts AFTER the outflow. Direction
        // matters — it tells in-flight detection which side to expect next.
        lagDays: Math.round((best.time - exp.time) / DAY_MS),
        outflow: exp.tx,
        inflow: best.tx,
      });
    }
  }

  return { ids, pairs, legs: { outflows: expenses, inflows: incomes } };
}

/**
 * @param {Array} transactions
 * @param {Object} [opts]  same options as detectTransferPairs
 * @returns {Set<string>} ids of transactions that are part of a transfer pair
 */
export function detectTransfers(transactions, opts = {}) {
  return detectTransferPairs(transactions, opts).ids;
}
